/**
 * Entry point for creating a brand-new template (Phase 8 of the WYSIWYG editor plan —
 * `templates/new-text` repointed here, replacing the deleted NewTextTemplatePage.tsx). Creates
 * just the Template shell, then hands off to TemplateEditorPage.tsx, which starts a template with
 * no version yet from a blank CanonicalDocument and creates version 1 on first Save. Existing
 * HTML/Markdown/docx templates need no migration — they already have a CDM blob (or one gets
 * built at upload time) and open in the same editor via its own "Edit" button.
 *
 * `?start=layout` (the default) shows the starter gallery; `?start=blank` the blank-document form.
 */
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useIsLiveWorkspace } from '../contexts/AppStateContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faFileCirclePlus, faFile, faTableCellsLarge } from '@fortawesome/free-solid-svg-icons'
import { templatesApiService } from '../services/templatesApiService'
import { ApiError } from '../services/BaseApiService'
import StarterGallery from '../components/starters/StarterGallery'
import LiveReadOnlyBanner from '../components/templates/LiveReadOnlyBanner'
import { colors, fontSize, radius, spacing } from '../styles/theme'

type StartMode = 'layout' | 'blank'

export default function NewTemplatePage() {
  // Live is read-only; templates are created in Test. Layouts can still be browsed there.
  const isLive = useIsLiveWorkspace()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const folderId = searchParams.get('folderId') ?? undefined
  const mode: StartMode = !isLive && searchParams.get('start') === 'blank' ? 'blank' : 'layout'

  function setMode(next: StartMode) {
    const params = new URLSearchParams(searchParams)
    params.set('start', next)
    setSearchParams(params, { replace: true })
  }

  const tab = (value: StartMode, label: string, icon: typeof faFile) => {
    const active = mode === value
    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => setMode(value)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer',
          borderRadius: radius.sm, fontSize: fontSize.base, fontWeight: active ? 600 : 400,
          border: `1px solid ${active ? colors.primary : colors.border}`,
          background: active ? colors.primaryLight : 'none',
          color: active ? colors.text : colors.textSecondary,
        }}
      >
        <FontAwesomeIcon icon={icon} />
        {label}
      </button>
    )
  }

  return (
    <div style={{ maxWidth: mode === 'layout' ? 1120 : 480, margin: '0 auto', padding: spacing.md, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.lg }}>
        <button
          type="button"
          onClick={() => navigate('/app/templates')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.textSecondary, fontSize: fontSize.base, cursor: 'pointer' }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>
        <h1 style={{ margin: 0, fontSize: fontSize.xl, fontWeight: 700, color: colors.text }}>
          New Template
        </h1>
      </div>

      {isLive ? <LiveReadOnlyBanner /> : (
        <div role="tablist" aria-label="How to start" style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.lg }}>
          {tab('layout', 'Start from a layout', faTableCellsLarge)}
          {tab('blank', 'Start blank', faFile)}
        </div>
      )}

      {mode === 'layout'
        ? <StarterGallery readOnly={isLive} initialFolderId={folderId} />
        : <NewTemplateForm folderId={folderId} />}

      {!isLive && (
        <p style={{ color: colors.textSecondary, fontSize: fontSize.sm, marginTop: spacing.lg }}>
          Have a Word document already? Use <strong>Upload Template(s)</strong> on the Templates page.
        </p>
      )}
    </div>
  )
}

function NewTemplateForm({ folderId }: { folderId?: string }) {
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) { setError('Name is required'); return }
    setError('')
    setLoading(true)
    try {
      const { templateId } = await templatesApiService.create({ name: name.trim(), folderId })
      navigate(`/app/templates/${templateId}/edit`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create template')
      setLoading(false)
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm, color: colors.textSecondary }}>
          Template name *
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="My template"
            style={{ padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.base, outline: 'none', boxSizing: 'border-box', width: '100%' }}
          />
        </label>

        <p style={{ color: colors.textSecondary, fontSize: fontSize.sm, margin: 0 }}>
          Opens a blank document in the WYSIWYG editor. You can add text, tables, images and
          <code style={{ margin: '0 4px' }}>{'{{variables}}'}</code>
          there, and save it as the template's first version.
        </p>

        {error && <div style={{ color: colors.error, fontSize: fontSize.sm }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }}>
          <button
            type="button"
            onClick={() => navigate('/app/templates')}
            style={{ padding: '8px 16px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.base, cursor: 'pointer', color: colors.textSecondary }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px', background: loading ? colors.textMuted : colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, fontSize: fontSize.base, fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}
          >
            <FontAwesomeIcon icon={faFileCirclePlus} />
            {loading ? 'Creating…' : 'Create & open editor'}
          </button>
        </div>
      </form>
    </div>
  )
}
